import { makeAutoObservable } from 'mobx';
import {
  determineNewDirection,
  isLongTrade,
} from './utils/mungingUtils/determineTradeDirection';
import { groupByFunction } from './utils/rxjs/groupByFunction';
import { determineUnderlying } from './utils/mungingUtils/determineUnderlying';
import ApplyRounding from '../../utils/math/ApplyRounding';
import determineSide from '../../utils/positions/DetermineSide';
import { translateOptionSymbol } from './utils/mungingUtils/translateOptionSymbol';
import { dateStringTo24HourEST } from './utils/mungingUtils/timeFormatting/dateStringTo24HourEST';
import { getFuturesTradingDay } from '../../utils/futuresTradingDay';
export class ClosedPositionsStore {
  closedPositionKeys = {
    sim: new Set(),
    live: new Set(),
  };
  closedPositions = {
    sim: {
      byPosition: {},
      bySymbol: {},
      byUnderlying: {},
      byTradePlan: {},
      byTemplate: {},
      byDay: {},
    },
    live: {
      byPosition: {},
      bySymbol: {},
      byUnderlying: {},
      byTradePlan: {},
      byTemplate: {},
      byDay: {},
    },
  };

  constructor(root) {
    makeAutoObservable(this);
    this.root = root;
    this.initializeStream();
  }

  initializeStream = () => {
    //closed positions stream
    this.closedPositionStream = groupByFunction(
      'messageType',
      'closedPosition',
      this.root.websocketSubject
    );
    this.closedPositionStream.subscribe((msg) =>
      this.handleClosedPositionMessage(msg)
    );
  };

  handleClosedPositionMessage = (msg) => {
    const accountType = msg.accountId === 'BACKTEST' ? 'sim' : 'live';
    const {
      positionId,
      tradingSystemId,
      pnl,
      quantity,
      date_acquired,
      date_exited,
      side,
      strategy,
      // strategyDescription,
      tradePlanTemplate,
      cost_basis,
      percent_pnl,
    } = msg.closedPosition;
    const symbol =
      msg.closedPosition.symbol.length > 6
        ? translateOptionSymbol(msg.closedPosition.symbol)
        : msg.closedPosition.symbol;
    const direction = isLongTrade(msg.closedPosition) ? 'LONG' : 'SHORT';
    const tradingDay = getFuturesTradingDay(date_exited);
    const closedPositionKey = [
      msg.accountId,
      positionId,
      tradingSystemId,
      symbol,
      date_acquired,
      date_exited,
      pnl,
      quantity,
    ].join('|');

    if (this.closedPositionKeys[accountType].has(closedPositionKey)) {
      return;
    }
    this.closedPositionKeys[accountType].add(closedPositionKey);

    if (msg.accountId === 'BACKTEST') {
      // byPosition
      this.closedPositions.sim.byPosition[positionId] = {
        ...msg.closedPosition,
        day: tradingDay,
        percent_pnl: Number(percent_pnl.split('%')[0]),
        quantity: ApplyRounding(quantity),
        date_acquired: dateStringTo24HourEST(date_acquired),
        date_exited: dateStringTo24HourEST(date_exited),
        side: determineSide(side),
        symbol,
        sortingField: new Date(date_acquired).getTime(),
      };

      // bySymbol
      if (this.closedPositions.sim.bySymbol[symbol]) {
        const newPnl =
          Number(this.closedPositions.sim.bySymbol[symbol].pnl) + Number(pnl);
        const newCostBasis =
          Number(this.closedPositions.sim.bySymbol[symbol].cost_basis) +
          Number(cost_basis);
        const newPercentPnl = ApplyRounding((newPnl / newCostBasis) * 100, 2);
        const newTotalTrades =
          this.closedPositions.sim.bySymbol[symbol].totalTrades + 1;
        this.closedPositions.sim.bySymbol[symbol].direction =
          determineNewDirection(
            this.closedPositions.sim.bySymbol[symbol].direction,
            direction
          );
        this.closedPositions.sim.bySymbol[symbol].pnl = newPnl;
        this.closedPositions.sim.bySymbol[symbol].percent_pnl = newPercentPnl;
        this.closedPositions.sim.bySymbol[symbol].cost_basis = newCostBasis;
        this.closedPositions.sim.bySymbol[symbol].totalTrades = newTotalTrades;
      } else {
        this.closedPositions.sim.bySymbol[symbol] = {
          symbol,
          direction,
          pnl,
          percent_pnl,
          cost_basis,
          totalTrades: 1,
          sortingField: symbol,
        };
      }

      // byUnderlying
      const underlying = determineUnderlying(symbol);
      if (this.closedPositions.sim.byUnderlying[underlying]) {
        const newPnl =
          Number(this.closedPositions.sim.byUnderlying[underlying].pnl) +
          Number(pnl);
        const newCostBasis =
          Number(this.closedPositions.sim.byUnderlying[underlying].cost_basis) +
          Number(cost_basis);
        const newPercentPnl = ApplyRounding((newPnl / newCostBasis) * 100, 2);
        const newTotalTrades =
          this.closedPositions.sim.byUnderlying[underlying].totalTrades + 1;
        this.closedPositions.sim.byUnderlying[underlying].direction =
          determineNewDirection(
            this.closedPositions.sim.byUnderlying[underlying].direction,
            direction
          );
        this.closedPositions.sim.byUnderlying[underlying].pnl = newPnl;
        this.closedPositions.sim.byUnderlying[underlying].percent_pnl =
          newPercentPnl;
        this.closedPositions.sim.byUnderlying[underlying].cost_basis =
          newCostBasis;
        this.closedPositions.sim.byUnderlying[underlying].totalTrades =
          newTotalTrades;
      } else {
        this.closedPositions.sim.byUnderlying[underlying] = {
          symbol: underlying,
          direction,
          pnl,
          percent_pnl,
          cost_basis,
          totalTrades: 1,
          sortingField: underlying,
        };
      }

      // byTradePlan
      if (this.closedPositions.sim.byTradePlan[tradingSystemId]) {
        const newPnl =
          Number(this.closedPositions.sim.byTradePlan[tradingSystemId].pnl) +
          Number(pnl);
        const newCostBasis =
          Number(
            this.closedPositions.sim.byTradePlan[tradingSystemId].cost_basis
          ) + Number(cost_basis);
        const newPercentPnl = ApplyRounding((newPnl / newCostBasis) * 100, 2);
        const newTotalTrades =
          this.closedPositions.sim.byTradePlan[tradingSystemId].totalTrades + 1;
        this.closedPositions.sim.byTradePlan[tradingSystemId].direction =
          determineNewDirection(
            this.closedPositions.sim.byTradePlan[tradingSystemId].direction,
            direction
          );
        this.closedPositions.sim.byTradePlan[tradingSystemId].pnl = newPnl;
        this.closedPositions.sim.byTradePlan[tradingSystemId].percent_pnl =
          newPercentPnl;
        this.closedPositions.sim.byTradePlan[tradingSystemId].cost_basis =
          newCostBasis;
        this.closedPositions.sim.byTradePlan[tradingSystemId].totalTrades =
          newTotalTrades;
      } else {
        this.closedPositions.sim.byTradePlan[tradingSystemId] = {
          symbol,
          direction,
          pnl,
          percent_pnl,
          cost_basis,
          totalTrades: 1,
          tradePlanTemplate,
          strategy,
          day: tradingDay,
          sortingField: tradePlanTemplate,
        };
      }

      // byTemplate
      if (this.closedPositions.sim.byTemplate[tradePlanTemplate]) {
        const newPnl =
          Number(this.closedPositions.sim.byTemplate[tradePlanTemplate].pnl) +
          Number(pnl);
        const newCostBasis =
          Number(
            this.closedPositions.sim.byTemplate[tradePlanTemplate].cost_basis
          ) + Number(cost_basis);
        const newPercentPnl = ApplyRounding((newPnl / newCostBasis) * 100, 2);
        const newTotalTrades =
          this.closedPositions.sim.byTemplate[tradePlanTemplate].totalTrades +
          1;
        this.closedPositions.sim.byTemplate[tradePlanTemplate].direction =
          determineNewDirection(
            this.closedPositions.sim.byTemplate[tradePlanTemplate].direction,
            direction
          );
        this.closedPositions.sim.byTemplate[tradePlanTemplate].pnl = newPnl;
        this.closedPositions.sim.byTemplate[tradePlanTemplate].percent_pnl =
          newPercentPnl;
        this.closedPositions.sim.byTemplate[tradePlanTemplate].cost_basis =
          newCostBasis;
        this.closedPositions.sim.byTemplate[tradePlanTemplate].totalTrades =
          newTotalTrades;
      } else {
        this.closedPositions.sim.byTemplate[tradePlanTemplate] = {
          direction,
          pnl,
          percent_pnl,
          cost_basis,
          totalTrades: 1,
          tradePlanTemplate,
          sortingField: tradePlanTemplate,
        };
      }
      // byDay
      const day = tradingDay;
      if (this.closedPositions.sim.byDay[day]) {
        const newPnl =
          Number(this.closedPositions.sim.byDay[day].pnl) + Number(pnl);
        const newCostBasis =
          Number(this.closedPositions.sim.byDay[day].cost_basis) +
          Number(cost_basis);
        const newPercentPnl = ApplyRounding((newPnl / newCostBasis) * 100, 2);
        const newTotalTrades =
          this.closedPositions.sim.byDay[day].totalTrades + 1;
        this.closedPositions.sim.byDay[day].direction = determineNewDirection(
          this.closedPositions.sim.byDay[day].direction,
          direction
        );
        this.closedPositions.sim.byDay[day].pnl = newPnl;
        this.closedPositions.sim.byDay[day].percent_pnl = newPercentPnl;
        this.closedPositions.sim.byDay[day].cost_basis = newCostBasis;
        this.closedPositions.sim.byDay[day].totalTrades = newTotalTrades;
      } else {
        this.closedPositions.sim.byDay[day] = {
          direction,
          pnl,
          percent_pnl,
          cost_basis,
          totalTrades: 1,
          day,
        };
      }
    } else {
      // byPosition
      this.closedPositions.live.byPosition[positionId] = {
        ...msg.closedPosition,
        day: tradingDay,
        percent_pnl: Number(percent_pnl.split('%')[0]),
        quantity: ApplyRounding(quantity),
        date_acquired: dateStringTo24HourEST(date_acquired),
        date_exited: dateStringTo24HourEST(date_exited),
        side: determineSide(side),
        symbol,
        sortingField: new Date(date_acquired).getTime(),
      };
      // bySymbol
      if (this.closedPositions.live.bySymbol[symbol]) {
        const newPnl =
          Number(this.closedPositions.live.bySymbol[symbol].pnl) + Number(pnl);
        const newCostBasis =
          Number(this.closedPositions.live.bySymbol[symbol].cost_basis) +
          Number(cost_basis);
        const newPercentPnl = ApplyRounding((newPnl / newCostBasis) * 100, 2);
        const newTotalTrades =
          this.closedPositions.live.bySymbol[symbol].totalTrades + 1;
        this.closedPositions.live.bySymbol[symbol].direction =
          determineNewDirection(
            this.closedPositions.live.bySymbol[symbol].direction,
            direction
          );
        this.closedPositions.live.bySymbol[symbol].pnl = newPnl;
        this.closedPositions.live.bySymbol[symbol].percent_pnl = newPercentPnl;
        this.closedPositions.live.bySymbol[symbol].cost_basis = newCostBasis;
        this.closedPositions.live.bySymbol[symbol].totalTrades = newTotalTrades;
      } else {
        this.closedPositions.live.bySymbol[symbol] = {
          symbol,
          direction,
          pnl,
          percent_pnl,
          cost_basis,
          totalTrades: 1,
          sortingField: symbol,
        };
      }

      // byUnderlying
      const underlying = determineUnderlying(symbol);
      if (this.closedPositions.live.byUnderlying[underlying]) {
        const newPnl =
          Number(this.closedPositions.live.byUnderlying[underlying].pnl) +
          Number(pnl);
        const newCostBasis =
          Number(
            this.closedPositions.live.byUnderlying[underlying].cost_basis
          ) + Number(cost_basis);
        const newPercentPnl = ApplyRounding((newPnl / newCostBasis) * 100, 2);
        const newTotalTrades =
          this.closedPositions.live.byUnderlying[underlying].totalTrades + 1;
        this.closedPositions.live.byUnderlying[underlying].direction =
          determineNewDirection(
            this.closedPositions.live.byUnderlying[underlying].direction,
            direction
          );
        this.closedPositions.live.byUnderlying[underlying].pnl = newPnl;
        this.closedPositions.live.byUnderlying[underlying].percent_pnl =
          newPercentPnl;
        this.closedPositions.live.byUnderlying[underlying].cost_basis =
          newCostBasis;
        this.closedPositions.live.byUnderlying[underlying].totalTrades =
          newTotalTrades;
      } else {
        this.closedPositions.live.byUnderlying[underlying] = {
          symbol: underlying,
          direction,
          pnl,
          percent_pnl,
          cost_basis,
          totalTrades: 1,
          sortingField: underlying,
        };
      }

      // byTradePlan
      if (this.closedPositions.live.byTradePlan[tradingSystemId]) {
        const newPnl =
          Number(this.closedPositions.live.byTradePlan[tradingSystemId].pnl) +
          Number(pnl);
        const newCostBasis =
          Number(
            this.closedPositions.live.byTradePlan[tradingSystemId].cost_basis
          ) + Number(cost_basis);
        const newPercentPnl = ApplyRounding((newPnl / newCostBasis) * 100, 2);
        const newTotalTrades =
          this.closedPositions.live.byTradePlan[tradingSystemId].totalTrades +
          1;
        this.closedPositions.live.byTradePlan[tradingSystemId].direction =
          determineNewDirection(
            this.closedPositions.live.byTradePlan[tradingSystemId].direction,
            direction
          );
        this.closedPositions.live.byTradePlan[tradingSystemId].pnl = newPnl;
        this.closedPositions.live.byTradePlan[tradingSystemId].percent_pnl =
          newPercentPnl;
        this.closedPositions.live.byTradePlan[tradingSystemId].cost_basis =
          newCostBasis;
        this.closedPositions.live.byTradePlan[tradingSystemId].totalTrades =
          newTotalTrades;
      } else {
        this.closedPositions.live.byTradePlan[tradingSystemId] = {
          symbol,
          direction,
          pnl,
          percent_pnl,
          cost_basis,
          totalTrades: 1,
          tradePlanTemplate,
          strategy,
          day: tradingDay,
          sortingField: tradePlanTemplate,
        };
      }

      // byTemplate
      if (this.closedPositions.live.byTemplate[tradePlanTemplate]) {
        const newPnl =
          Number(this.closedPositions.live.byTemplate[tradePlanTemplate].pnl) +
          Number(pnl);
        const newCostBasis =
          Number(
            this.closedPositions.live.byTemplate[tradePlanTemplate].cost_basis
          ) + Number(cost_basis);
        const newPercentPnl = ApplyRounding((newPnl / newCostBasis) * 100, 2);
        const newTotalTrades =
          this.closedPositions.live.byTemplate[tradePlanTemplate].totalTrades +
          1;
        this.closedPositions.live.byTemplate[tradePlanTemplate].direction =
          determineNewDirection(
            this.closedPositions.live.byTemplate[tradePlanTemplate].direction,
            direction
          );
        this.closedPositions.live.byTemplate[tradePlanTemplate].pnl = newPnl;
        this.closedPositions.live.byTemplate[tradePlanTemplate].percent_pnl =
          newPercentPnl;
        this.closedPositions.live.byTemplate[tradePlanTemplate].cost_basis =
          newCostBasis;
        this.closedPositions.live.byTemplate[tradePlanTemplate].totalTrades =
          newTotalTrades;
      } else {
        this.closedPositions.live.byTemplate[tradePlanTemplate] = {
          direction,
          pnl,
          percent_pnl,
          cost_basis,
          totalTrades: 1,
          tradePlanTemplate,
          sortingField: tradePlanTemplate,
        };
      }
      // byDay
      const day = tradingDay;
      if (this.closedPositions.live.byDay[day]) {
        const newPnl =
          Number(this.closedPositions.live.byDay[day].pnl) + Number(pnl);
        const newCostBasis =
          Number(this.closedPositions.live.byDay[day].cost_basis) +
          Number(cost_basis);
        const newPercentPnl = ApplyRounding((newPnl / newCostBasis) * 100, 2);
        const newTotalTrades =
          this.closedPositions.live.byDay[day].totalTrades + 1;
        this.closedPositions.live.byDay[day].direction = determineNewDirection(
          this.closedPositions.live.byDay[day].direction,
          direction
        );
        this.closedPositions.live.byDay[day].pnl = newPnl;
        this.closedPositions.live.byDay[day].percent_pnl = newPercentPnl;
        this.closedPositions.live.byDay[day].cost_basis = newCostBasis;
        this.closedPositions.live.byDay[day].totalTrades = newTotalTrades;
      } else {
        this.closedPositions.live.byDay[day] = {
          direction,
          pnl,
          percent_pnl,
          cost_basis,
          totalTrades: 1,
          day,
        };
      }
    }
  };
}
